import { AddNewDeal } from '@crm/features';
import { renderDealCardsList } from '@crm/pages/deals/lib';
import { BoardColumn } from '@crm/pages/deals/ui';
import { cn } from '@crm/shared/lib';

import { AddColumnBtn } from './AddColumnBtn';
import { ColumnPreviewFrame } from './ColumnPreviewFrame';
import { FALLBACK_COLUMN_BG_COLOR, FALLBACK_COLUMN_COLOR } from '../../config';
import { useAddColumn } from '../../model/add-column/useAddColumn';
import { useEditColumn } from '../../model/edit-column';
import { EditColumn } from '../EditColumn';

import type { TDealColumn, TDealColumnColor } from '@crm/entities/deals-board';

type DealsPageMainProps = {
    activeDealId: string | null;
    columnColors: TDealColumnColor[];
    columns: TDealColumn[];
    hideColumnDropPlaceholder?: boolean;
};

export const DealsPageMain = ({
    activeDealId,
    columnColors,
    columns,
    hideColumnDropPlaceholder = false,
}: DealsPageMainProps) => {
    const {
        isEditMode,
        previewColumnBgColor,
        previewColumnColor,
        openEditMode,
        closeEditMode,
        changePreviewColumnColor,
        handleCreateColumnSubmit,
    } = useEditColumn();
    const { scrollContainerRef, handleAddColumnClick } = useAddColumn({
        onAddCb: openEditMode,
    });

    const visibleColumnColor = previewColumnColor ?? FALLBACK_COLUMN_COLOR;
    const visibleColumnBgColor = previewColumnBgColor ?? FALLBACK_COLUMN_BG_COLOR;

    return (
        <main
            ref={scrollContainerRef}
            className={cn(
                'flex min-h-0 flex-1 items-stretch gap-3 overflow-hidden overflow-x-auto',
                hideColumnDropPlaceholder &&
                    '[&_[data-deal-cards-list]_[data-dnd-placeholder=clone]]:hidden'
            )}
        >
            {columns.map((column, index) => {
                const columnId = String(column.id);
                const isEmptyColumn = column.deals?.length === 0;

                return (
                    <BoardColumn
                        key={column.id}
                        columnId={column.id}
                        columnColors={columnColors}
                        index={index}
                        bgColor={column.bgColor}
                        headerText={column.title}
                        color={column.color}
                        showPlaceholder={column?.deals.length === 0}
                        isDefault={column?.isDefault}
                        isEmpty={isEmptyColumn}
                        main={renderDealCardsList(columnId, columnId, column.deals, {
                            activeDealId,
                            hideActiveDealPlaceholder: hideColumnDropPlaceholder,
                        })}
                        footer={
                            <AddNewDeal
                                columnId={column.isDefault ? undefined : column.id}
                                triggerBtnVariant="secondary"
                                dialogTriggerStyles="w-full"
                                triggerBtnStyles="w-full"
                            />
                        }
                    />
                );
            })}
            {isEditMode && (
                <ColumnPreviewFrame color={visibleColumnColor} bgColor={visibleColumnBgColor}>
                    <EditColumn
                        columnColors={columnColors}
                        defaultColor={FALLBACK_COLUMN_COLOR}
                        onClose={closeEditMode}
                        onSubmit={handleCreateColumnSubmit}
                        onColorPreviewChange={changePreviewColumnColor}
                    />
                </ColumnPreviewFrame>
            )}
            {!isEditMode && <AddColumnBtn onClick={handleAddColumnClick} />}
        </main>
    );
};
